import axios from 'axios';

// Since axios.defaults.baseURL is already set in index.js, we just need the path
const API_PATH = '/api/monitoring';

/**
 * Monitoring Service
 * Frontend service for interacting with monitoring API
 */
class MonitoringService {
  constructor() {
    // Check if monitoring API is available
    this.checkAvailability();
  }

  /**
   * Check if monitoring API is available
   */
  async checkAvailability() {
    try {
      const response = await axios.get('/api/monitoring/health', { timeout: 5000 });
      if (response.data.success && response.data.status === 'available') {
        window.__monitoringAvailable = true;
        console.log('Monitoring API is available');
        return true;
      }
    } catch (error) {
      window.__monitoringAvailable = false;
      console.warn('Monitoring API not available:', error.message);
      return false;
    }
  }

  /**
   * Get dashboard overview data
   */
  async getDashboard(timeRange = '24h', clientId = null) {
    try {
      const params = { timeRange };
      if (clientId && clientId !== 'all') {
        params.clientId = clientId;
      }
      const response = await axios.get(`${API_PATH}/dashboard`, { params });
      return response.data.data;
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
      throw error;
    }
  }

  /**
   * Get monitoring events with filters
   */
  async getEvents(filters = {}) {
    try {
      const response = await axios.get(`${API_PATH}/events`, {
        params: filters
      });
      return response.data.data;
    } catch (error) {
      console.error('Failed to fetch events:', error);
      throw error;
    }
  }

  /**
   * Get statistics
   */
  async getStatistics(params = {}) {
    try {
      const response = await axios.get(`${API_PATH}/statistics`, { params });
      return response.data.data;
    } catch (error) {
      console.error('Failed to fetch statistics:', error);
      throw error;
    }
  }

  /**
   * Update event status (acknowledge, resolve, ignore)
   */
  async updateEventStatus(eventId, status, resolution = '') {
    try {
      const response = await axios.put(`${API_PATH}/events/${eventId}`, {
        status,
        resolution
      });
      return response.data.data;
    } catch (error) {
      console.error('Failed to update event status:', error);
      throw error;
    }
  }

  /**
   * Get login history
   */
  async getLoginHistory(filters = {}) {
    try {
      const response = await axios.get(`${API_PATH}/login-history`, {
        params: filters
      });
      return response.data.data;
    } catch (error) {
      console.error('Failed to fetch login history:', error);
      throw error;
    }
  }

  /**
   * Get error logs
   */
  async getErrorLogs(filters = {}) {
    try {
      const response = await axios.get(`${API_PATH}/errors`, {
        params: filters
      });
      return response.data.data;
    } catch (error) {
      console.error('Failed to fetch error logs:', error);
      throw error;
    }
  }

  /**
   * Log frontend error
   */
  async logFrontendError(errorData) {
    try {
      // Check if monitoring endpoint exists before trying to log
      if (!window.__monitoringAvailable) {
        console.warn('Monitoring endpoint not available, skipping error log');
        return null;
      }

      const response = await axios.post(`${API_PATH}/log/frontend-error`, errorData);
      return response.data.data;
    } catch (error) {
      // If 404, disable monitoring to prevent cascade
      if (error.response?.status === 404) {
        window.__monitoringAvailable = false;
        console.warn('Monitoring endpoint returned 404, disabling error logging');
      }
      // Don't throw - we don't want error logging to crash the app
    }
  }

  /**
   * Log custom event (for debugging)
   */
  async logCustomEvent(eventType, details) {
    try {
      await this.logFrontendError({
        message: `Custom Event: ${eventType}`,
        stack: JSON.stringify(details),
        componentStack: '',
        url: window.location.href,
        userAgent: navigator.userAgent
      });
    } catch (error) {
      console.error('Failed to log custom event:', error);
    }
  }
}

export const monitoringService = new MonitoringService();

/**
 * Error Logger Utility
 * Automatically logs errors to monitoring system
 */
export class ErrorLogger {
  static instance = null;

  static getInstance() {
    if (!ErrorLogger.instance) {
      ErrorLogger.instance = new ErrorLogger();
    }
    return ErrorLogger.instance;
  }

  constructor() {
    this.setupAxiosInterceptors();
    this.setupGlobalErrorHandler();
  }

  /**
   * Setup Axios interceptors to catch API errors
   */
  setupAxiosInterceptors() {
    axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        // Don't log errors from monitoring endpoints to avoid cascade
        if (error.config?.url?.includes('/api/monitoring')) {
          return Promise.reject(error);
        }

        if (error.response) {
          // API error with response
          await monitoringService.logFrontendError({
            message: `API Error: ${error.response.status} - ${error.response.statusText}`,
            stack: JSON.stringify({
              url: error.config?.url,
              method: error.config?.method,
              status: error.response.status,
              data: error.response.data
            }),
            componentStack: '',
            url: window.location.href,
            userAgent: navigator.userAgent,
            severity: error.response.status >= 500 ? 'high' : 'medium'
          });
        } else if (error.request) {
          // Network error
          await monitoringService.logFrontendError({
            message: 'Network Error: No response received',
            stack: JSON.stringify({
              url: error.config?.url,
              method: error.config?.method
            }),
            componentStack: '',
            url: window.location.href,
            userAgent: navigator.userAgent,
            severity: 'high'
          });
        }

        return Promise.reject(error);
      }
    );
  }

  /**
   * Setup global error handler for unhandled errors
   */
  setupGlobalErrorHandler() {
    window.addEventListener('error', async (event) => {
      await monitoringService.logFrontendError({
        message: event.message,
        stack: event.error?.stack || '',
        componentStack: '',
        url: window.location.href,
        userAgent: navigator.userAgent,
        severity: 'medium',
        metadata: {
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno
        }
      });
    });

    window.addEventListener('unhandledrejection', async (event) => {
      await monitoringService.logFrontendError({
        message: `Unhandled Promise Rejection: ${event.reason}`,
        stack: event.reason?.stack || JSON.stringify(event.reason),
        componentStack: '',
        url: window.location.href,
        userAgent: navigator.userAgent,
        severity: 'medium'
      });
    });
  }

  /**
   * Manually log an error
   */
  async logError(error, severity = 'medium', metadata = {}) {
    await monitoringService.logFrontendError({
      message: error.message || String(error),
      stack: error.stack || '',
      componentStack: '',
      url: window.location.href,
      userAgent: navigator.userAgent,
      severity,
      metadata
    });
  }

  /**
   * Log performance issue
   */
  async logPerformance(operation, duration, threshold) {
    if (duration > threshold) {
      await monitoringService.logFrontendError({
        message: `Performance Issue: ${operation} took ${duration}ms (threshold: ${threshold}ms)`,
        stack: '',
        componentStack: '',
        url: window.location.href,
        userAgent: navigator.userAgent,
        severity: duration > threshold * 2 ? 'high' : 'medium',
        metadata: {
          operation,
          duration,
          threshold
        }
      });
    }
  }
}

// Initialize error logger
ErrorLogger.getInstance();

export default monitoringService;
